import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle, Target, XCircle } from 'lucide-react';
import { format, isAfter, parseISO } from 'date-fns';
import { Panel, StatCard, StatusPill, EmptyState, ProgressBar } from '../ui';
import { filterLeadProjects, formatTime } from './utils';

const ProjectLeadStatusDashboard = ({
  projects = [],
  tasks = [],
  timeEntries = [],
  currentUserId,
}) => {
  const managedProjects = useMemo(
    () => filterLeadProjects(projects, currentUserId),
    [projects, currentUserId]
  );

  const projectStatuses = managedProjects.map((project) => {
    const projectTasks = tasks.filter((task) => task.project_id === project.id);
    const projectEntries = timeEntries.filter((entry) => entry.project_id === project.id);
    const totalTasks = projectTasks.length;
    const completedTasks = projectTasks.filter((task) => task.status === 'Done').length;
    const inProgressTasks = projectTasks.filter((task) => task.status === 'In Progress').length;
    const overdueTasks = projectTasks.filter((task) => {
      if (!task.due_date || task.status === 'Done') return false;
      return isAfter(new Date(), parseISO(task.due_date));
    }).length;
    const totalTime = projectEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
    const billableTime = projectEntries
      .filter((entry) => entry.is_billable)
      .reduce((sum, entry) => sum + (entry.duration || 0), 0);
    const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    const efficiency = totalTime > 0 ? (billableTime / totalTime) * 100 : 0;

    let status = 'Done';
    let statusText = 'On Track';
    if (completionRate < 30) {
      status = 'Blocked';
      statusText = 'Critical';
    } else if (completionRate < 50 || overdueTasks > totalTasks * 0.3) {
      status = 'At Risk';
      statusText = 'At Risk';
    }

    return {
      ...project,
      totalTasks,
      completedTasks,
      inProgressTasks,
      overdueTasks,
      totalTime,
      completionRate,
      efficiency,
      status,
      statusText,
    };
  });

  const overallStats = {
    totalProjects: managedProjects.length,
    healthyProjects: projectStatuses.filter((p) => p.status === 'Done').length,
    atRiskProjects: projectStatuses.filter((p) => p.status === 'At Risk').length,
    criticalProjects: projectStatuses.filter((p) => p.status === 'Blocked').length,
  };

  return (
    <div className="space-y-6" data-testid="project-lead-status-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Target} tone="blue" label="My Projects" value={overallStats.totalProjects} />
        <StatCard
          icon={CheckCircle}
          tone="green"
          label="On Track"
          value={overallStats.healthyProjects}
        />
        <StatCard
          icon={AlertTriangle}
          tone="amber"
          label="At Risk"
          value={overallStats.atRiskProjects}
        />
        <StatCard
          icon={XCircle}
          tone="red"
          label="Critical"
          value={overallStats.criticalProjects}
        />
      </div>

      <Panel>
        <h3 className="text-lg font-semibold mb-6 text-secondary-900">Project Status Details</h3>
        {projectStatuses.length === 0 ? (
          <EmptyState
            title="No managed projects"
            description="Projects you created or are assigned to will appear here."
          />
        ) : (
          <div className="space-y-4">
            {projectStatuses.map((project) => (
              <div
                key={project.id}
                className="p-6 bg-secondary-50 rounded-lg"
                data-testid={`lead-project-${project.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-secondary-900 mb-2">
                      {project.name}
                    </h4>
                    <p className="text-sm text-secondary-600 mb-2">
                      {project.description || 'No description available'}
                    </p>
                    {project.created_at && (
                      <div className="text-sm text-secondary-500">
                        Created: {format(new Date(project.created_at), 'MMM dd, yyyy')}
                      </div>
                    )}
                  </div>
                  <StatusPill status={project.status} label={project.statusText} />
                </div>
                <ProgressBar value={project.completionRate} className="mb-4" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center p-3 bg-white dark:bg-secondary-700 rounded-lg">
                    <div className="font-semibold text-secondary-900">
                      {project.completedTasks}/{project.totalTasks}
                    </div>
                    <div className="text-secondary-600">Tasks</div>
                  </div>
                  <div className="text-center p-3 bg-white dark:bg-secondary-700 rounded-lg">
                    <div className="font-semibold text-blue-600">{project.inProgressTasks}</div>
                    <div className="text-secondary-600">In Progress</div>
                  </div>
                  <div className="text-center p-3 bg-white dark:bg-secondary-700 rounded-lg">
                    <div className="font-semibold text-red-600">{project.overdueTasks}</div>
                    <div className="text-secondary-600">Overdue</div>
                  </div>
                  <div className="text-center p-3 bg-white dark:bg-secondary-700 rounded-lg">
                    <div className="font-semibold text-green-600">
                      {formatTime(project.totalTime)}
                    </div>
                    <div className="text-secondary-600">Total Time</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default ProjectLeadStatusDashboard;
